import React from "react";

import { Edit, Trash2 } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Address } from "~/types/client";

interface AddressCardProps {
  address: Address;
  onEdit: (address: Address) => void;
  onDelete: (addressId: string) => void;
  isFiscalAddress?: boolean;
  onSetAsFiscal?: (address: Address) => void;
}

const AddressCard: React.FC<AddressCardProps> = ({
  address,
  onEdit,
  onDelete,
  isFiscalAddress = false,
  onSetAsFiscal,
}) => {
  return (
    <Card key={address.id}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base font-semibold">
              {address.street} {address.number}
            </CardTitle>
            {(address.isDefault || isFiscalAddress) && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {address.isDefault && (
                  <Badge variant="secondary">Principal</Badge>
                )}
                {isFiscalAddress && (
                  <Badge
                    variant="outline"
                    className="border-amber-200 bg-amber-50 text-amber-700"
                  >
                    Fiscal
                  </Badge>
                )}
              </div>
            )}
          </div>
          <div className="flex shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => onEdit(address)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-red-600"
              onClick={() => onDelete(address.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-1 text-sm text-muted-foreground">
          {address.complement && <p>{address.complement}</p>}
          {address.comuna && <p>{address.comuna}</p>}
          <p>
            {address.city}
            {address.state ? `, ${address.state}` : ""}
          </p>
        </div>
      </CardContent>
      {onSetAsFiscal && !isFiscalAddress && (
        <CardFooter className="pt-0">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 text-muted-foreground hover:text-foreground"
            onClick={() => onSetAsFiscal(address)}
          >
            Establecer como fiscal
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default AddressCard;
